// src/components/TickersTable.tsx
import { useState } from 'react'
import { useTickers, useCreateTicker, useUpdateTicker, useDeleteTicker } from '../hooks/useTickers.ts'
import { useCategories } from '../hooks/useCategories.ts'
import { TickerModal } from './TickerModal.tsx'
import type { Ticker } from '../types.ts'

interface Props {
  userId: number
}

type ModalState =
  | { open: false }
  | { open: true; ticker?: Ticker }   // undefined = criar, definido = editar

export function TickersTable({ userId }: Props) {
  const { data, isLoading, isError, error } = useTickers(userId)
  const { data: categories = [] } = useCategories(userId)
  const createMutation = useCreateTicker(userId)
  const updateMutation = useUpdateTicker(userId)
  const deleteMutation = useDeleteTicker(userId)

  const [modal, setModal] = useState<ModalState>({ open: false })
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const isSaving = createMutation.isPending || updateMutation.isPending

  function openCreate() { setModal({ open: true }) }
  function openEdit(ticker: Ticker) { setModal({ open: true, ticker }) }
  function closeModal() { setModal({ open: false }) }

  function handleSave(formData: {
    ticker: string
    cnpj: string | null
    qtd: string | null
    preco_medio: string | null
    category_id: number | null
  }) {
    if (modal.open && modal.ticker) {
      updateMutation.mutate(
        { id: modal.ticker.id, data: formData },
        { onSuccess: closeModal },
      )
    } else {
      createMutation.mutate(
        { ...formData, user_id: userId },
        { onSuccess: closeModal },
      )
    }
  }

  function handleDelete(id: number, ticker: string) {
    const confirmed = window.confirm(`Confirma excluir o ticker "${ticker}"?`)
    if (!confirmed) return

    setDeletingId(id)
    deleteMutation.mutate(id, { onSettled: () => setDeletingId(null) })
  }

  if (isLoading) return <div className="status">Carregando tickers...</div>

  if (isError) {
    return (
      <div className="status status--error">
        Erro: {error instanceof Error ? error.message : 'Erro desconhecido'}
      </div>
    )
  }

  const formatDecimal = (value?: string | null) => {
    if (value === null || value === undefined || value === '') return '—'
    const num = Number(value)
    if (Number.isNaN(num)) return value
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6,
    })
  }

  const formatFlexibleDecimal = (value?: string | null) => {
    if (value === null || value === undefined || value === '') return '—'
    const num = Number(value)
    if (Number.isNaN(num)) return value
    return num.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 20,
    })
  }

  const categoriesById = new Map(categories.map((category) => [category.id, category]))

  const getCategoryLabel = (categoryId: number | null) => {
    if (!categoryId) return '—'
    return categoriesById.get(categoryId)?.name ?? '—'
  }

  return (
    <>
      <div className="card-toolbar">
        <span className="badge">{data?.length ?? 0} tickers</span>
        <button className="btn btn--primary btn--sm" onClick={openCreate}>
          + Novo Ticker
        </button>
      </div>

      {!data || data.length === 0 ? (
        <div className="status">Nenhum ticker cadastrado.</div>
      ) : (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>Ticker</th>
                <th>Qtd</th>
                <th>Preco medio</th>
                <th>CNPJ</th>
                <th>Categoria</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.map((t) => (
                <tr key={t.id}>
                  <td><strong>{t.ticker}</strong></td>
                  <td>{formatFlexibleDecimal(t.qtd)}</td>
                  <td>{formatDecimal(t.preco_medio)}</td>
                  <td>{t.cnpj ?? '—'}</td>
                  <td>{getCategoryLabel(t.category_id)}</td>
                  <td className="table-actions">
                    <button
                      className="btn btn--ghost btn--sm"
                      onClick={() => openEdit(t)}
                      title="Editar"
                    >
                      ✎
                    </button>
                    <button
                      className="btn btn--danger btn--sm"
                      onClick={() => handleDelete(t.id, t.ticker)}
                      disabled={deletingId === t.id}
                      title="Deletar"
                    >
                      {deletingId === t.id ? '…' : '✕'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modal.open && (
        <TickerModal
          ticker={modal.ticker}
          categories={categories}
          userId={userId}
          onClose={closeModal}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </>
  )
}
